import React, { useMemo, useState } from 'react';
import apiService from '../../services/ApiService';
import { Button } from '../ui/Button';
import { BadgeRenderer } from './BadgeRenderer';
import type { BadgeDesign, BadgeDefinition } from '../../types/discoveryBadges';

const SHAPES = ['roundel', 'circle', 'shield', 'hexagon', 'rounded_rectangle', 'pennant', 'crest', 'plaque'] as const;
const ICONS = ['trophy', 'laurel', 'checkered_flag', 'steering_wheel', 'helmet', 'wheel', 'engine', 'turbo', 'wrench', 'track', 'shield', 'star', 'club_crest'] as const;
const FINISHES = ['flat', 'enamel', 'metallic', 'embroidered'] as const;

const PURPOSES = [
  { id: 'participation', label: 'Participation', category: 'participation', prestige: 'participation', trigger: 'checked_in', method: 'automatic' },
  { id: 'award', label: 'Award / placement', category: 'award', prestige: 'award', trigger: 'award_published', method: 'automatic' },
  { id: 'special', label: 'Organizer’s choice / special', category: 'special', prestige: 'award', trigger: 'manual', method: 'manual' },
  { id: 'invitational', label: 'Invitational', category: 'invitational', prestige: 'participation', trigger: 'featured', method: 'manual' },
  { id: 'affiliation', label: 'Club membership', category: 'affiliation', prestige: 'affiliation', trigger: 'membership_active', method: 'automatic' },
  { id: 'featured', label: 'Featured build', category: 'featured', prestige: 'affiliation', trigger: 'featured', method: 'manual' },
] as const;

interface BadgeCreatorProps {
  sourceType: 'event' | 'club' | 'platform';
  sourceId: number;
  onSaved?: (def: BadgeDefinition) => void;
}

function contrastRatio(fg: string, bg: string): number {
  const lum = (hex: string) => {
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    const c = (x: number) => (x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4));
    return 0.2126 * c(r) + 0.7152 * c(g) + 0.0722 * c(b);
  };
  const L1 = lum(fg);
  const L2 = lum(bg);
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
}

export const BadgeCreator: React.FC<BadgeCreatorProps> = ({ sourceType, sourceId, onSaved }) => {
  const [step, setStep] = useState(0);
  const [purposeId, setPurposeId] = useState<(typeof PURPOSES)[number]['id']>('participation');
  const [name, setName] = useState('');
  const [shortLabel, setShortLabel] = useState('');
  const [yearLabel, setYearLabel] = useState(String(new Date().getFullYear()));
  const [shape, setShape] = useState<(typeof SHAPES)[number]>('roundel');
  const [icon, setIcon] = useState<(typeof ICONS)[number]>('trophy');
  const [finish, setFinish] = useState<(typeof FINISHES)[number]>('enamel');
  const [primary, setPrimary] = useState('#1A1A1A');
  const [secondary, setSecondary] = useState('#F97316');
  const [accent, setAccent] = useState('#FAFAFA');
  const [placement, setPlacement] = useState('');
  const [awardTitle, setAwardTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const purpose = PURPOSES.find((p) => p.id === purposeId) || PURPOSES[0];
  const filteredPurposes = PURPOSES.filter((p) => {
    if (sourceType === 'club') return ['affiliation', 'featured', 'special', 'participation'].includes(p.id);
    if (sourceType === 'event') return ['participation', 'award', 'special', 'invitational'].includes(p.id);
    return true;
  });

  const design: BadgeDesign = useMemo(
    () => ({
      name: name || 'Preview',
      short_label: shortLabel || 'LABEL',
      year_label: yearLabel || null,
      badge_category: purpose.category as any,
      prestige_tier: purpose.prestige as any,
      template_key: 'classic_roundel',
      shape: shape as any,
      primary_color: primary,
      secondary_color: secondary,
      accent_color: accent,
      icon_key: icon,
      border_style: 'thin',
      finish_style: finish as any,
      source_type: sourceType,
      source_id: sourceId,
    }),
    [name, shortLabel, yearLabel, purpose, shape, primary, secondary, accent, icon, finish, sourceType, sourceId]
  );

  const contrastOk = contrastRatio(accent, primary) >= 3;
  const steps = ['Purpose', 'Appearance', 'Label', 'Criteria', 'Preview'];

  const save = async (activate: boolean) => {
    setBusy(true);
    setError(null);
    try {
      if (!contrastOk) throw new Error('Accent color needs more contrast against the primary color');
      if (!shortLabel.trim() || shortLabel.trim().length > 18) throw new Error('Short label required (max 18 characters)');
      const criteria: Record<string, string> = { trigger: purpose.trigger };
      if (placement) criteria.placement = placement;
      if (awardTitle) criteria.award_title = awardTitle;
      const res = await apiService.saveBadgeDefinition({
        source_type: sourceType,
        source_id: sourceId,
        name: name || shortLabel,
        short_label: shortLabel.trim(),
        year_label: yearLabel || null,
        badge_category: purpose.category,
        prestige_tier: purpose.prestige,
        shape,
        icon_key: icon,
        finish_style: finish,
        primary_color: primary,
        secondary_color: secondary,
        accent_color: accent,
        issuance_method: purpose.method,
        criteria,
      });
      const def = (res as any).data as BadgeDefinition;
      if (activate && def?.id) {
        await apiService.activateBadgeDefinition(def.id, 'activate');
      }
      onSaved?.(def);
    } catch (e: any) {
      setError(e.message || 'Failed to save badge');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="form-section">
      <div className="flex gap-2 overflow-x-auto mb-4 text-xs text-gray-400">
        {steps.map((s, i) => (
          <button
            key={s}
            type="button"
            className={`px-2 py-1 rounded ${i === step ? 'bg-orange-500/20 text-orange-300' : ''}`}
            onClick={() => setStep(i)}
          >
            {i + 1}. {s}
          </button>
        ))}
      </div>

      {error ? (
        <div role="alert" className="mb-3 text-red-300 text-sm">
          {error}
        </div>
      ) : null}

      {step === 0 ? (
        <div className="grid gap-2">
          {filteredPurposes.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`text-left rounded-lg border px-3 py-3 min-h-[44px] ${
                purposeId === p.id ? 'border-orange-500/50 bg-orange-500/10' : 'border-white/[0.06]'
              }`}
              onClick={() => setPurposeId(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      ) : null}

      {step === 1 ? (
        <div className="space-y-4">
          <fieldset>
            <legend className="text-sm text-gray-300 mb-2">Shape</legend>
            <div className="flex flex-wrap gap-2">
              {SHAPES.map((s) => (
                <button key={s} type="button" className={shape === s ? 'filter-chip-active' : 'filter-chip'} onClick={() => setShape(s)}>
                  {s.replace('_', ' ')}
                </button>
              ))}
            </div>
          </fieldset>
          <div className="grid grid-cols-3 gap-3">
            <label className="text-xs text-gray-400">
              Primary
              <input type="color" className="mt-1 w-full h-11" value={primary} onChange={(e) => setPrimary(e.target.value.toUpperCase())} />
            </label>
            <label className="text-xs text-gray-400">
              Secondary
              <input type="color" className="mt-1 w-full h-11" value={secondary} onChange={(e) => setSecondary(e.target.value.toUpperCase())} />
            </label>
            <label className="text-xs text-gray-400">
              Accent
              <input type="color" className="mt-1 w-full h-11" value={accent} onChange={(e) => setAccent(e.target.value.toUpperCase())} />
            </label>
          </div>
          <fieldset>
            <legend className="text-sm text-gray-300 mb-2">Icon</legend>
            <div className="flex flex-wrap gap-2">
              {ICONS.map((i) => (
                <button key={i} type="button" className={icon === i ? 'filter-chip-active' : 'filter-chip'} onClick={() => setIcon(i)}>
                  {i.replace('_', ' ')}
                </button>
              ))}
            </div>
          </fieldset>
          <label className="block text-sm text-gray-300">
            Finish
            <select className="input mt-1" value={finish} onChange={(e) => setFinish(e.target.value as any)}>
              {FINISHES.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {step === 2 ? (
        <div className="space-y-3">
          <label className="block text-sm">
            Name
            <input className="input mt-1" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="block text-sm">
            Short label (max 18)
            <input
              maxLength={18}
              className="input mt-1"
              value={shortLabel}
              onChange={(e) => setShortLabel(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Year
            <input className="input mt-1" value={yearLabel} onChange={(e) => setYearLabel(e.target.value)} />
          </label>
        </div>
      ) : null}

      {step === 3 ? (
        <div className="space-y-3 text-sm text-gray-300">
          <p>
            Trigger: <strong className="text-white">{purpose.trigger}</strong> ({purpose.method})
          </p>
          {purpose.category === 'award' ? (
            <>
              <label className="block">
                Award title (optional match)
                <input className="input mt-1" value={awardTitle} onChange={(e) => setAwardTitle(e.target.value)} />
              </label>
              <label className="block">
                Placement (optional match)
                <input className="input mt-1" value={placement} onChange={(e) => setPlacement(e.target.value)} placeholder="1st" />
              </label>
            </>
          ) : null}
        </div>
      ) : null}

      {step === 4 ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-[#0a0a0a] p-4 flex flex-col items-center gap-2">
              <p className="text-xs text-gray-500">Dark surface</p>
              <BadgeRenderer design={design} size="lg" surface="dark" />
            </div>
            <div className="rounded-lg bg-gray-100 p-4 flex flex-col items-center gap-2">
              <p className="text-xs text-gray-600">Light surface</p>
              <BadgeRenderer design={design} size="lg" surface="light" />
            </div>
          </div>
          <p className={`text-sm ${contrastOk ? 'text-emerald-400' : 'text-red-300'}`}>
            Contrast: {contrastOk ? 'Pass' : 'Fail: adjust accent or primary'}
          </p>
          <div className="rounded-lg border border-white/[0.06] p-3 flex items-center gap-3">
            <div className="w-16 h-12 rounded bg-[#141414]" aria-hidden />
            <div>
              <p className="text-sm text-white">Vehicle card preview</p>
              <BadgeRenderer design={design} size="md" />
            </div>
          </div>
        </div>
      ) : null}

      <div className="sticky bottom-0 mt-4 flex gap-2 bg-[#0c0c0c]/95 border-t border-white/[0.06] py-3 -mx-1 px-1">
        <Button type="button" variant="ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
          Back
        </Button>
        {step < steps.length - 1 ? (
          <Button type="button" variant="primary" className="flex-1" onClick={() => setStep((s) => s + 1)}>
            Next
          </Button>
        ) : (
          <>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => save(false)}>
              Save draft
            </Button>
            <Button type="button" variant="primary" disabled={busy || !contrastOk} onClick={() => save(true)}>
              Activate
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default BadgeCreator;
